import { useQuery, useQueryClient, useMutation } from 'react-query'
import { apiService } from '@/services/apiService'
import { useAuthStore } from '@/stores/authStore'

const MY_KEY = ['cottage-request', 'mine'] as const
const PENDING_KEY = ['cottage-requests', 'PENDING'] as const
const DECIDED_KEY = ['cottage-requests', 'DECIDED'] as const

/**
 * The signed-in user's most-recent cottage membership request, or null.
 * Drives the Cottage tab on /profile.
 */
export function useMyCottageRequest() {
  return useQuery({
    queryKey: MY_KEY,
    queryFn: () => apiService.getMyCottageRequest(),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
  })
}

export function useCreateCottageRequest() {
  const qc = useQueryClient()
  return useMutation(
    (input: { cottageId: string; note?: string }) => apiService.createCottageRequest(input),
    {
      onSuccess: () => {
        qc.invalidateQueries(MY_KEY)
        qc.invalidateQueries(PENDING_KEY)
      },
    }
  )
}

export function useCancelCottageRequest() {
  const qc = useQueryClient()
  return useMutation(() => apiService.cancelMyCottageRequest(), {
    onSuccess: () => {
      qc.invalidateQueries(MY_KEY)
      qc.invalidateQueries(PENDING_KEY)
    },
  })
}

/**
 * Pending count for the admin nav badge. Only fires when the caller is ADMIN
 * — non-admins get a `disabled` query returning `data: undefined`.
 */
export function usePendingRequestCount() {
  const role = useAuthStore((s) => s.user?.role)
  const enabled = role === 'ADMIN'
  const query = useQuery({
    queryKey: PENDING_KEY,
    queryFn: () => apiService.listCottageRequests('PENDING'),
    enabled,
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
    refetchOnWindowFocus: true,
  })
  return { count: query.data?.length ?? 0, ...query }
}

/** Admin: full pending list. Same key as usePendingRequestCount. */
export function usePendingCottageRequests() {
  const role = useAuthStore((s) => s.user?.role)
  return useQuery({
    queryKey: PENDING_KEY,
    queryFn: () => apiService.listCottageRequests('PENDING'),
    enabled: role === 'ADMIN',
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  })
}

/** Admin: decided history (most recent 30). */
export function useDecidedCottageRequests(enabled: boolean) {
  const role = useAuthStore((s) => s.user?.role)
  return useQuery({
    queryKey: DECIDED_KEY,
    queryFn: () => apiService.listCottageRequests('DECIDED'),
    enabled: enabled && role === 'ADMIN',
    staleTime: 30 * 1000,
  })
}

export function useApproveCottageRequest() {
  const qc = useQueryClient()
  return useMutation((id: string) => apiService.approveCottageRequest(id), {
    onSuccess: () => {
      qc.invalidateQueries(PENDING_KEY)
      qc.invalidateQueries(DECIDED_KEY)
    },
  })
}

export function useRejectCottageRequest() {
  const qc = useQueryClient()
  return useMutation(
    ({ id, reviewerNote }: { id: string; reviewerNote?: string }) =>
      apiService.rejectCottageRequest(id, reviewerNote),
    {
      onSuccess: () => {
        qc.invalidateQueries(PENDING_KEY)
        qc.invalidateQueries(DECIDED_KEY)
      },
    }
  )
}
